import { ChangeDetectorRef, Component, ComponentRef, Input, OnChanges,
         OnDestroy, OnInit, SimpleChanges, ViewContainerRef, Inject
       } from '@angular/core';
import { StageService } from 'collage-designer/stage/stage.service';
import { StageUtils } from 'core/utils.stage';
import { Subscription } from 'rxjs';

import { FontInjectorService
       } from '../../collage-designer/font-injector.service';
import { TextPlaceholder
       } from '../../collage-designer/stage/placeholder/text-placeholder';
import { GlobalConstants } from '../../core/constants';
import { Globals } from '../../core/globals';
import { TextApplianceResult, TextController, TextEditingResult,
       } from '../../text/text.controller';
import { FontDescription, normalizeFactor, Text, TextAlignment, TextChunk,
         TextChunkKind, updateText, TextEffects,
       } from '../../text/text.model';
import { ToastComponent } from '../toast/toast.component';
import { determineFeedbackMessage, EditingAction
       } from './text-editing.messages';
import { FontVariant } from './text-effects-chooser.component';

interface ControlsVisibility
{
  alignment: boolean;
  font: boolean;
  color: boolean;
  fontSize: boolean;
}

@Component({
    selector: 'text-editor',
    templateUrl: './text-editing.component.html',
    styleUrls: ['./text-editing.component.scss'],
    standalone: false
})
export class TextEditingComponent implements OnChanges, OnDestroy, OnInit
{
  @Input('placeholder') public placeholder: TextPlaceholder;
  @Input('isBoxTitle') public isBoxTitle: boolean = false;
  public textModel: Text | undefined = undefined;
  public bold: boolean = false;
  public italic: boolean = false;
  public isTextEmpty: boolean = true;
  public viewMode: string = 'fonts';

  public controls: ControlsVisibility = {
    alignment: false,
    font: false,
    color: false,
    fontSize: false,
  };

  private controller: TextController | undefined = undefined;
  private toast: ComponentRef<ToastComponent>;
  private initialModelText: string = '';
  private placeholderSubscription: Subscription | undefined = undefined;

  public constructor(
      @Inject(StageService) private stageService: StageService,
      private fontInjectorService: FontInjectorService,
      private changeDetector: ChangeDetectorRef,
      private vcr: ViewContainerRef)
  {
  }

  public async ngOnInit(): Promise<void>
  {
    this.toast = this.vcr.createComponent(ToastComponent);
    document.body.appendChild(this.toast.location.nativeElement);
  }

  public ngOnChanges(changes: SimpleChanges): void
  {
    if ('placeholder' in changes) {
      if (!changes['placeholder'].currentValue) {
        return;
      }
      const placeholder = changes['placeholder'].currentValue;
      this.controller = placeholder.zoomModeTextController;

      this.unsubscribePlaceholder();
      this.placeholderSubscription = placeholder.changed.subscribe(
          () => this.setTextModel(placeholder.getTextModel()));

      const model = placeholder.getTextModel();
      this.setTextModel(model);
      this.initialModelText = this.trimmed(model.text);
      this.setControlsVisibility(placeholder);
    }
  }

  public ngOnDestroy() {
    this.unsubscribePlaceholder();
    document.body.removeChild(this.toast.location.nativeElement);
  }

  private unsubscribePlaceholder(): void
  {
    if (this.placeholderSubscription) {
      this.placeholderSubscription.unsubscribe();
      this.placeholderSubscription = undefined;
    }
  }

  public async clearText(): Promise<void>
  {
    await this.evaluateControllerResult(
      EditingAction.ChangeText,
      this.controller.setText(this.editedModel, []),
      true);
  }

  public onEffectChanged(effects: TextEffects): void
  {
    this.evaluateControllerResult(
      EditingAction.ChangeEffects,
      this.controller.setEffects(
        this.editedModel,
        effects,
        {minFontSize: this.placeholder.minFontSizePt}));
  }

  public async onFontVariantChanged(variant: FontVariant): Promise<void>
  {
    await this.applyFont(this.textModel.fontName, variant.bold, variant.italic);
  }

  public onAlignmentChanged(alignment: TextAlignment): void
  {
    this.evaluateControllerResult(
      EditingAction.Unspecified,
      this.controller.setHorizontalAlignment(this.editedModel, alignment));
  }

  public async onFontNameChanged(fontName: string): Promise<void>
  {
    await this.applyFont(fontName, this.bold, this.italic);
  }

  private async applyFont(
      fontName: string, bold: boolean, italic: boolean): Promise<void>
  {
    let font: FontDescription;

    try {
      font = await this.fontInjectorService.addFont(fontName, bold, italic);
    }
    catch (e) {
      await this.fontInjectorService.buildFontLoadingError(
          e, 'TextEditingComponent');
      return;
    }

    this.evaluateControllerResult(
      EditingAction.ChangeFont,
      this.controller.setFont(
        this.editedModel,
        font,
        {minFontSize: this.placeholder.minFontSizePt}));
    this.changeDetector.detectChanges();

    this.changeDetector.reattach();
    StageUtils.removeUnusedFonts(
        this.stageService.getCurrentStageReference());
  }

  public onColorChanged(color: string): void
  {
    this.evaluateControllerResult(
      EditingAction.Unspecified,
      this.controller.setColor(this.editedModel, color));
  }

  public onBgColorChanged(color: string): void
  {
    this.evaluateControllerResult(
      EditingAction.Unspecified,
      this.controller.setBgColor(this.editedModel, color));
  }

  public get normalizedFontSize(): number
  {
    return this.textModel.fontSize / this.fontSizeNormalizeFactor;
  }

  public get minNormalizedFontSize(): number
  {
    return Math.ceil(
        this.placeholder.minFontSizePt / this.fontSizeNormalizeFactor);
  }

  public get maxNormalizedFontSize(): number
  {
    return Math.floor(
        this.placeholder.maxFontSizePt / this.fontSizeNormalizeFactor);
  }

  private get fontSizeNormalizeFactor(): number
  {
    return normalizeFactor(this.textModel.fontDescription);
  }

  public onFontSizeChanged(normalizedFontSize: number): void
  {
    this.evaluateControllerResult(
      EditingAction.ChangeFontSize,
      this.controller.setFontSize(
        this.editedModel,
        normalizedFontSize * this.fontSizeNormalizeFactor,
        {
          minFontSize: this.placeholder.minFontSizePt,
          maxFontSize: this.placeholder.maxFontSizePt,
        }));
  }

  private get editedModel(): Text
  {
    return {
      ...this.textModel,
      availableArea: this.placeholder.getTextModel().availableArea,
    };
  }

  private async evaluateControllerResult(
    action: EditingAction,
    result: TextEditingResult,
    forceChanged: boolean = false): Promise<void>
  {
    this.setTextModel(result.model);

    const feedbackMessage = determineFeedbackMessage(
        action, result, {
          flexible: this.placeholder.isFlexible(),
          zoomMode: this.placeholder.zoomMode
        });

    if (feedbackMessage !== undefined) {
      this.toast.instance.show(feedbackMessage);
    }

    if (result.type === TextApplianceResult.Success) {
      await this.apply(forceChanged);
    }
  }

  private setTextModel(model: Text): void
  {
    const text = model.text.filter(
        el => el.kind !== TextChunkKind.Control);
    this.textModel = updateText(model, text);

    this.bold = !!this.textModel.fontBold;
    this.italic = !!this.textModel.fontItalic;
    this.isTextEmpty = this.textModel.text.length === 0;
  }

  public async apply(forceChanged: boolean = false): Promise<void>
  {
    const pel = this.placeholder.element.node;
    const componentType = StageUtils.getComponentType(
        this.placeholder.element.root());
    const placeholderContext = {
      id: this.placeholder.element.attr('id'),
      kind: StageUtils.PlaceholderKind.TEXT,
      node: pel,
    };

    const isChanged = forceChanged ||
        Globals.mode === GlobalConstants.MODE_GOD ||
        (StageUtils.isVolatileText(componentType, placeholderContext) &&
         this.trimmed(this.textModel.text) !== this.initialModelText);

    this.placeholder.setTextModel(this.textModel);
    await this.placeholder.commitContent(isChanged);
  }

  private setControlsVisibility(placeholder: TextPlaceholder): void
  {
    this.controls = {
      alignment: placeholder.alignmentChangeable,
      font: placeholder.fontChangeable,
      color: placeholder.colorChangeable,
      fontSize: placeholder.fontSizeChangeable,
    };
  }

  public switchView(mode: string): void
  {
    this.viewMode = mode;
  }

  private trimmed(chunks: Array<TextChunk>): string
  {
    return chunks.map(c => c.content ? c.content.trim() : '').join('');
  }
}
